/**
 * Reglas del listado único de productos. La RPC aplica las mismas vistas;
 * aquí están para poder probarlas sin base de datos.
 *
 * «Marca = proveedor» solo cuenta proveedores con show_brand = false:
 * JLC es marca pública y no entra. La caducidad de sync es la del catálogo.
 */

import { catalogSyncStaleMaxHours, isStaleSync } from '../catalog/catalogSyncFreshness';

export const PRODUCT_VIEWS = [
    'all',
    'published',
    'on_sale',
    'draft',
    'review',
    'incomplete',
    'out',
    'inactive',
    'stale',
    'brand',
] as const;

export type ProductViewId = (typeof PRODUCT_VIEWS)[number];

export const PRODUCT_VIEW_LABELS: Record<ProductViewId, string> = {
    all: 'Todos',
    published: 'Publicadas',
    on_sale: 'A la venta',
    draft: 'Borrador',
    review: 'En revisión',
    incomplete: 'Publicadas incompletas',
    out: 'Agotados',
    inactive: 'Inactivos',
    stale: 'Sync caducada',
    brand: 'Marca = proveedor',
};

export const PRODUCT_PAGE_SIZE = 50;
export const PRODUCT_SELECT_CAP = 2000;
export const PRODUCT_FILTER_KEYS = ['origin', 'brand', 'collection', 'stock'] as const;
export const PRODUCT_SORTS = ['name', 'price', 'sync', 'completeness'] as const;

export const PRODUCT_INDEX_URL = {
    views: PRODUCT_VIEWS,
    defaultView: 'all' as const,
    filterKeys: PRODUCT_FILTER_KEYS,
};

/** `supplier` es el mismo filtro que `origin`. Si vienen los dos, manda origin. */
export function productOriginAlias(params: URLSearchParams): { origin: string | null; dropSupplier: boolean } {
    const supplier = params.get('supplier')?.trim() || '';
    const origin = params.get('origin')?.trim() || '';
    if (!supplier) return { origin: origin || null, dropSupplier: false };
    return { origin: origin || supplier, dropSupplier: true };
}

export const STOCK_FILTERS = [
    { id: 'in_stock', label: 'Disponible' },
    { id: 'out_of_stock', label: 'Agotado' },
    { id: 'on_order', label: 'Bajo pedido' },
    { id: 'unlimited', label: 'Sin límite' },
] as const;

export interface SupplierBrandFlag {
    name: string;
    showBrand: boolean;
}

export interface ListedProductFacts {
    kind: 'catalog' | 'merch';
    isActive: boolean;
    qualityStatus: string | null;
    completeness: number | null;
    stockCode: string;
    supplierSlug: string | null;
    supplierActive: boolean;
    lastSyncAt: string | null;
    brand: string;
}

/** La marca del producto es el nombre de un proveedor que no debe verse como marca. */
export function brandEqualsHiddenSupplier(
    brand: string,
    suppliers: readonly SupplierBrandFlag[],
): boolean {
    const value = brand.trim().toLowerCase();
    if (!value) return false;
    return suppliers.some((supplier) => (
        supplier.showBrand === false && supplier.name.trim().toLowerCase() === value
    ));
}

export function productInView(
    product: ListedProductFacts,
    view: ProductViewId,
    suppliers: readonly SupplierBrandFlag[],
    nowMs: number,
): boolean {
    const publishedCatalog = product.kind === 'catalog' && product.qualityStatus === 'hero';
    const onSale = publishedCatalog && product.isActive;
    switch (view) {
        case 'all':
            return true;
        case 'published':
            return publishedCatalog || (product.kind === 'merch' && product.isActive);
        case 'on_sale':
            return onSale || (product.kind === 'merch' && product.isActive);
        case 'draft':
            return product.kind === 'catalog' && product.qualityStatus === 'draft';
        case 'review':
            return product.kind === 'catalog' && product.qualityStatus === 'review';
        case 'incomplete':
            return onSale && product.completeness != null && product.completeness < 4;
        case 'out':
            return product.stockCode === 'out_of_stock';
        case 'inactive':
            return !product.isActive;
        case 'stale':
            return product.kind === 'catalog'
                && product.supplierActive
                && isStaleSync(
                    product.lastSyncAt,
                    catalogSyncStaleMaxHours(product.supplierSlug ?? ''),
                    nowMs,
                );
        case 'brand':
            return product.kind === 'catalog'
                && product.isActive
                && brandEqualsHiddenSupplier(product.brand, suppliers);
        default:
            return false;
    }
}

export function countByView(
    products: readonly ListedProductFacts[],
    suppliers: readonly SupplierBrandFlag[],
    nowMs: number,
): Record<ProductViewId, number> {
    const counts = Object.fromEntries(PRODUCT_VIEWS.map((view) => [view, 0])) as Record<ProductViewId, number>;
    for (const product of products) {
        for (const view of PRODUCT_VIEWS) {
            if (productInView(product, view, suppliers, nowMs)) counts[view] += 1;
        }
    }
    return counts;
}

export function stockLabel(code: string, units: number | null): string {
    if (code === 'unlimited') return 'Sin límite';
    if (code === 'out_of_stock') return 'Agotado';
    if (code === 'on_order') return 'Bajo pedido';
    if (code === 'in_stock' && units != null) return `${units} uds`;
    if (code === 'in_stock') return 'Disponible';
    return '—';
}

/** Euros de margen. Sin coste, o sin precio, no hay cifra. */
export function marginEuros(
    priceMin: number | null,
    priceMax: number | null,
    cost: number | null,
): { min: number; max: number } | null {
    if (cost == null || priceMin == null || !Number.isFinite(cost) || !Number.isFinite(priceMin)) return null;
    const max = priceMax == null || !Number.isFinite(priceMax) ? priceMin : priceMax;
    return { min: priceMin - cost, max: max - cost };
}

export function publicationStatus(product: { kind: string; isActive: boolean; qualityStatus: string | null }): string {
    if (product.kind === 'merch') return product.isActive ? 'hero' : 'inactive';
    return product.qualityStatus || 'draft';
}
